import { Maybe, SalaryType } from '@/lib/generated/graphql-types'
import { formatList } from '@/lib/strings'
import {
  CONTRACTING_ENTITY_AGENCY_ID,
  MALPRACTICE_BENEFIT_ID,
} from '@/modules/onboarding/locum-prn-wrap-up/constants'
import { TRAVEL_BENEFIT_ID } from '@/modules/onboarding/locum-prn-wrap-up/constants'
import { LODGING_BENEFIT_ID } from '@/modules/onboarding/locum-prn-wrap-up/constants'
import {
  formatEmployerTypeDetails,
  formatJobType,
  formatTaxStatus,
  getEstimatedFte,
  getTimeAgo,
} from '@/modules/salary/common/utils'
import {
  CompUtilItem,
  OverviewItem,
  SalaryDetails,
} from '@/modules/salary/types'
import { OnboardingFieldNames } from '@/modules/surveys/configs/onboarding/base-config'
import {
  insuranceHealthBenefits,
  LOAN_ASSISTANCE,
  PARENTAL_LEAVE,
  RetirementPlan,
  satisfactionOptions,
  tailCoverageDisplay,
} from '@/modules/surveys/configs/onboarding/benefits-config'
import {
  CALL_SCHEDULE_NONE_VALUE,
  CALL_TYPE_AT_HOME_VALUE,
  CALL_TYPE_BOTH_VALUE,
  CALL_TYPE_IN_HOUSE_VALUE,
  DAYS_VALUE,
  LOCUM_TENENS_VALUE,
  NIGHTS_VALUE,
  practiceSettingOptions,
  PRN_VALUE,
  scheduledHoursOptions,
  VARIABLE_SCHEDULE_VALUE,
  VARIABLE_SCHEDULED_HOURS_VALUE,
  VARIABLE_SHIFT_TIMING_VALUE,
  WEEKDAYS_ONLY_VALUE,
} from '@/modules/surveys/configs/onboarding/current-job-pane-config'
import { formatAbbreviatedDollar } from '@/utils/currency-formatters'
import {
  AlertTriangle,
  Baby,
  Bed,
  Building2,
  CalendarDays,
  CircleDollarSign,
  CircleHelp,
  Clock,
  FileHeart,
  Hospital,
  House,
  PhoneCall,
  Plane,
  SquareCheckBig,
  SquareUser,
  Star,
  TabletSmartphone,
  TreePalm,
} from 'lucide-react'

export function getSalaryDetails(
  salary: SalaryType | undefined,
): SalaryDetails {
  if (!salary) return {} as SalaryDetails
  return {
    academicAffiliation: salary.academicAffiliation || '',
    ancillaryIncomeSource: salary.ancillaryIncomeSource || '',
    avgHoursWorked: `${salary.avgHoursWorked} hours/wk`,
    avgHoursWorkedNumber: salary.avgHoursWorked || 0,
    baseSalary: salary.baseSalary,
    anyBonuses: salary.anyBonuses,
    anyOtherIncome: salary.anyOtherIncome,
    benefits: getSalaryBenefits(salary),
    bonus: salary.bonus || 0,
    callSchedule: salary.callSchedule || '',
    location: salary.displayLocation || '',
    compModel: salary.compModel || '',
    jobType: salary.jobType || '',
    compBonuses: getCompBonuses(
      salary.productivityBonus || 0,
      salary.qualityBonus || 0,
      salary.otherBonus || 0,
    ),
    compSatisfaction: getCompSatisfaction(
      salary.compSatisfaction || '',
      salary.occupation?.profession.name || '',
    ),
    disclaimer: getDisclaimer({
      newJob: salary.newJob || false,
      signingBonus: salary.signingBonus || 0,
      relocationBonus: salary.relocationBonus || 0,
      residencyStipend: salary.residencyStipend || 0,
      professionTitle: salary.occupation?.profession.name || '',
      compLoanAssistance: salary.compLoanAssistance || 0,
    }),
    employerTypeDetails: formatEmployerTypeDetails(
      salary.employerType,
      salary.employerTypeDetails,
      salary.practiceSetting,
      salary.jobType === PRN_VALUE || salary.jobType === LOCUM_TENENS_VALUE,
    ),
    jobTaxStatus: getJobTypeTaxStatus(salary.jobType || '', salary.taxStatus),
    otherIncomes: getOtherIncomes(
      salary.annualLeadershipStipend || 0,
      salary.overtimeIncome || 0,
      salary.overtimeRatePerHour || 0,
      salary.annualEstPartnerIncome || 0,
      salary.annualEstRankAdjustment || 0,
      salary.annualNonClinicalIncome || 0,
      salary.annualEstAncillaryIncome || 0,
      salary.ancillaryIncomeSource || '',
    ),
    annualEstAncillaryIncome: salary.annualEstAncillaryIncome || 0,
    nightlyHourlyRate: salary.nightlyHourlyRate || 0,
    weekendHourlyRate: salary.weekendHourlyRate || 0,
    collectionsPct: salary.collectionsPct || 0,
    hourlyRateDifferential: salary.hourlyRateDifferential || false,
    basePayPerHour: salary.basePayPerHour || 0,
    wrvuRate: salary.wrvuRate || 0,
    compTargetWrvu: salary.compTargetWrvu || 0,
    otherNoteworthyComp: salary.otherNoteworthyComp || '',
    postedOn: `Posted ${getTimeAgo(salary.postedAt)} ago`,
    professionTitle: salary.occupation?.profession.name || '',
    responsibilities: salary.responsibilities,
    salaryId: salary.salaryId,
    specialtyTitle: salary.occupation?.baseTitlePlusSpecialty || '',
    submittedByCurrentUser: salary.submittedByCurrentUser || false,
    workOverview: [
      getResponsibilities(
        salary.responsibilities,
        salary.clinicalPct || 0,
        salary.leadershipPct || 0,
        salary.teachingPct || 0,
        salary.researchPct || 0,
        salary.jobType || '',
      ),
      getWorkload(
        salary.avgHoursWorked || 0,
        salary.locums?.guaranteedHrsPerDay || 0,
      ),
      getShiftSchedule(
        salary.scheduledDays || '',
        salary.scheduledHours || '',
        salary.shiftTiming || '',
      ),
      getOnCallSchedule(
        salary.callSchedule || '',
        salary.callType || '',
        salary.callPaid || false,
        salary.callPaidRate,
        salary.dayOffPostCall || false,
        salary.callScheduleOther || '',
      ),
    ].filter((item) => item.enabled),
    jobOverview: [
      getJobTypeAndFTE(salary),
      getEmployerInformation(salary),
      getSettingInformation(salary),
    ].filter((item) => item.enabled),
    agencyInformation: [
      getAgencyName(salary),
      getAgencyTravel(salary),
      getAgencyLodging(salary),
      getAgencyMalpractice(salary),
      getAgencyRating(salary),
    ].filter((item) => item.enabled),
    additionalDetails: salary.callScheduleDetails || '',
    yearsOfExperience: salary.yearsOfExperience || '',
    compTotal: salary.compTotal,
    locums: salary.locums,
    practiceSetting: salary.practiceSetting || '',
    practiceSettingOther: salary.practiceSettingOther || '',
    overtimeRatePerHour: salary.overtimeRatePerHour || 0,
  }
}

export function getJobTypeTaxStatus(
  jobType: Maybe<string> | undefined,
  taxStatus: string[],
) {
  return `${formatJobType(jobType ?? '')} (${formatTaxStatus(taxStatus)})`
}

function getJobTypeAndFTE(salary: SalaryType) {
  const value = [
    getJobTypeTaxStatus(salary.jobType || '', salary.taxStatus),
    salary.estimatedFte ? getEstimatedFte(salary.estimatedFte) : '',
  ]
    .filter(Boolean)
    .join(' · ')

  return {
    icon: SquareUser,
    text: 'Job',
    subText: value,
    enabled: value.length > 0,
  }
}

function getEmployerInformation(salary: SalaryType) {
  let value = formatEmployerTypeDetails(
    salary.employerType,
    salary.employerTypeDetails,
    salary.practiceSetting,
    salary.jobType === PRN_VALUE || salary.jobType === LOCUM_TENENS_VALUE,
  )
  if (salary.jobType === PRN_VALUE || salary.jobType === LOCUM_TENENS_VALUE) {
    value = salary.locums?.employerName || ''
  }
  return {
    icon: Hospital,
    text: 'Employer',
    subText: value ?? '',
    enabled: !!value,
  }
}

function getSettingInformation(salary: SalaryType) {
  return {
    icon: Bed,
    text: 'Setting',
    subText:
      practiceSettingOptions.find(
        (option) => option.id === salary.practiceSetting,
      )?.label || '',
    enabled: !!salary.practiceSetting,
  }
}

function getCompBonuses(
  productivityBonus: number,
  qualityBonus: number,
  otherBonus: number,
): CompUtilItem[] {
  const bonuses = []
  if (productivityBonus > 0) {
    bonuses.push({
      label: 'Productivity Bonus',
      value: productivityBonus,
      singleItemLabel: 'Productivity',
      id: OnboardingFieldNames.CompensationProductivityBonus,
    })
  }
  if (qualityBonus > 0) {
    bonuses.push({
      label: 'Quality Bonus',
      value: qualityBonus,
      singleItemLabel: 'Quality',
      id: OnboardingFieldNames.CompensationQualityBonus,
    })
  }
  if (otherBonus > 0) {
    bonuses.push({
      label: 'Other Bonus',
      value: otherBonus,
      singleItemLabel: 'Other',
      id: OnboardingFieldNames.CompensationOtherBonus,
    })
  }
  return bonuses
}

function getOtherIncomes(
  leadershipStipend: number,
  overtime: number,
  overtimeRatePerHour: number,
  partnerIncome: number,
  rankAdjustment: number,
  nonClinical: number,
  ancillary: number,
  ancillaryIncomeSource: string,
): CompUtilItem[] {
  const otherIncomes = []
  if (leadershipStipend > 0) {
    otherIncomes.push({
      label: 'Leadership Stipend',
      singleItemLabel: 'Leadership',
      value: leadershipStipend,
      id: OnboardingFieldNames.CompensationOtherLeadershipStipend,
    })
  }
  if (overtime > 0) {
    otherIncomes.push({
      id: OnboardingFieldNames.CompensationOtherOvertimeIncome,
      label: 'Overtime',
      singleItemLabel: 'Overtime',
      value: overtime,
      overtimeRatePerHour,
    })
  }
  if (partnerIncome > 0) {
    otherIncomes.push({
      id: OnboardingFieldNames.CompensationOtherPartnerIncome,
      label: 'Partner Income',
      singleItemLabel: 'Partner Income',
      value: partnerIncome,
    })
  }
  if (rankAdjustment > 0) {
    otherIncomes.push({
      label: 'Rank Adjustment',
      singleItemLabel: 'Rank Adjustment',
      value: rankAdjustment,
      id: OnboardingFieldNames.CompensationOtherRankAdjustment,
    })
  }
  if (nonClinical > 0) {
    otherIncomes.push({
      label: 'Non Clinical',
      singleItemLabel: 'Non Clinical',
      value: nonClinical,
      id: OnboardingFieldNames.CompensationOtherNonClinicalIncome,
    })
  }
  if (ancillary > 0) {
    otherIncomes.push({
      id: OnboardingFieldNames.CompensationOtherOtherAncillaryIncome,
      label: `Ancillary Income${ancillaryIncomeSource ? ' *' : ''}`,
      singleItemLabel: `Ancillary Income${ancillaryIncomeSource ? ' *' : ''}`,
      value: ancillary,
      ancillaryIncomeSource,
    })
  }
  return otherIncomes
}

function getResponsibilities(
  responsibilities: string[] | undefined | null,
  clinicalPct: number,
  leadershipPct: number,
  teachingPct: number,
  researchPct: number,
  jobType: string,
) {
  const pctBreakdown = []
  if (!responsibilities)
    return {
      icon: SquareCheckBig,
      text: 'Responsibilities',
      list: [],
      enabled: false,
    }
  if (responsibilities.length === 1 && responsibilities.includes('clinical')) {
    pctBreakdown.push('Clinical Only')
  } else {
    if (responsibilities.includes('clinical') && clinicalPct > 0) {
      pctBreakdown.push(`Clinical (${clinicalPct}%)`)
    }
    if (responsibilities.includes('leadership') && leadershipPct > 0) {
      pctBreakdown.push(`Leadership (${leadershipPct}%)`)
    }
    if (responsibilities.includes('teaching') && teachingPct > 0) {
      pctBreakdown.push(`Teaching (${teachingPct}%)`)
    }
    if (responsibilities.includes('research') && researchPct > 0) {
      pctBreakdown.push(`Research (${researchPct}%)`)
    }
  }
  const isLocumsOrPrn = jobType === PRN_VALUE || jobType === LOCUM_TENENS_VALUE
  return {
    icon: SquareCheckBig,
    text: 'Responsibilities',
    list: pctBreakdown,
    enabled: !isLocumsOrPrn && pctBreakdown.length > 0,
  }
}

function getWorkload(avgHoursWorked: number, guaranteedHrsPerDay: number) {
  const list = []
  if (guaranteedHrsPerDay > 0) {
    list.push(`Guaranteed ${guaranteedHrsPerDay} hrs/day`)
  }
  if (avgHoursWorked > 0) {
    list.push(`Average ${avgHoursWorked} hrs/week`)
  }
  return {
    icon: Clock,
    text: 'Hours Per Week',
    list,
    enabled: list.length > 0,
  }
}

const scheduledHoursMap = Object.entries(scheduledHoursOptions).reduce(
  (acc, [_index, { id, label }]) => {
    acc[id] = label
    return acc
  },
  {} as Record<string, string>,
)

export function getShiftSchedule(
  scheduledDays: string,
  scheduledHours: string[] | undefined | null,
  shiftTiming: string,
) {
  const scheduledDaysFormattedMap: Record<string, string> = {
    [VARIABLE_SCHEDULE_VALUE]: 'Variable days of week',
    [WEEKDAYS_ONLY_VALUE]: 'M-F',
  }
  const scheduledDaysFormatted =
    scheduledDaysFormattedMap[scheduledDays] ??
    // need to check if scheduledDays is a string and contains weekdays
    // as values from Fillout are different from the ones in the config
    (scheduledDays.toLowerCase().includes('weekdays')
      ? scheduledDaysFormattedMap[WEEKDAYS_ONLY_VALUE]
      : scheduledDaysFormattedMap[VARIABLE_SCHEDULE_VALUE])

  const scheduledTimingFormattedMap: Record<string, string> = {
    [VARIABLE_SHIFT_TIMING_VALUE]: 'day & night shifts',
    [DAYS_VALUE]: 'day shifts',
    [NIGHTS_VALUE]: 'night shifts',
  }

  const scheduledHoursFormatted = formatList(
    scheduledHours
      ? scheduledHours?.map((hour) => {
          if (hour === VARIABLE_SCHEDULED_HOURS_VALUE) {
            return 'variable hours'
          }
          return scheduledHoursMap[hour] ?? hour
        })
      : [],
  )

  return {
    icon: CalendarDays,
    text: 'Schedule',
    subText: [
      scheduledDaysFormatted,
      scheduledTimingFormattedMap[shiftTiming] || shiftTiming || '',
      scheduledHoursFormatted,
    ]
      .filter(Boolean)
      .join(', '),
    enabled:
      scheduledDays.length > 1 ||
      Boolean(scheduledHours && scheduledHours.length > 1),
  }
}

function getOnCallSchedule(
  callType: string | null,
  callSchedule: string | null,
  callPaid: boolean,
  callPaidRate: string | null | undefined,
  dayOffPostCall: boolean,
  callScheduleOther: string | null,
) {
  const list = []
  if (callSchedule === CALL_TYPE_IN_HOUSE_VALUE) {
    list.push(`On call ${callType} (in-house)`)
  } else if (callSchedule === CALL_TYPE_AT_HOME_VALUE) {
    list.push(`On call ${callType} (at-home)`)
  } else if (callSchedule === CALL_TYPE_BOTH_VALUE) {
    list.push(`On call ${callType} (in-house & at-home)`)
  }
  if (callPaid) {
    const paidRate = parseFloat(callPaidRate ?? '')
    if (!Number.isNaN(paidRate) && paidRate > 0) {
      list.push(`paid ($${callPaidRate}/night)`)
    } else {
      list.push(`paid`)
    }
  }
  if (dayOffPostCall) {
    list.push('day off post call')
  }
  if (callScheduleOther) {
    list.push(callScheduleOther)
  }
  return {
    icon: PhoneCall,
    text: 'Call',
    list: list,
    enabled: callType !== CALL_SCHEDULE_NONE_VALUE && list.length > 0,
  }
}

function getDisclaimer({
  newJob,
  signingBonus,
  relocationBonus,
  residencyStipend,
  professionTitle,
  compLoanAssistance,
}: {
  newJob: boolean
  signingBonus: number
  relocationBonus: number
  residencyStipend: number
  professionTitle: string
  compLoanAssistance: number
}) {
  let disclaimer = ''
  const bonuses = []
  if (signingBonus > 0) {
    bonuses.push(`signing bonus (${formatAbbreviatedDollar(signingBonus)})`)
  }
  if (relocationBonus > 0) {
    bonuses.push(
      `relocation expenses (${formatAbbreviatedDollar(relocationBonus)})`,
    )
  }
  if (residencyStipend > 0) {
    bonuses.push(
      `residency stipend (${formatAbbreviatedDollar(residencyStipend)})`,
    )
  }
  if (compLoanAssistance > 0) {
    bonuses.push(
      `loan assistance (${formatAbbreviatedDollar(compLoanAssistance)})`,
    )
  }
  if (bonuses.length > 0) {
    if (newJob) {
      disclaimer += `This is a new job and this ${professionTitle} also received a one-time `
    } else {
      disclaimer += `This ${professionTitle} received a one-time `
    }
  }

  if (bonuses.length === 1) {
    disclaimer += bonuses[0]
  } else if (bonuses.length === 2) {
    disclaimer += bonuses.join(' and ')
  } else if (bonuses.length > 2) {
    disclaimer +=
      bonuses.slice(0, -1).join(', ') + ', and ' + bonuses.slice(-1)[0]
  }

  if (disclaimer.length > 0) {
    disclaimer += '.'
  }
  return disclaimer
}

function getCompSatisfaction(compSatisfaction: string, profession: string) {
  const option = satisfactionOptions.find(
    (option) => option.id === compSatisfaction,
  )
  return {
    id: compSatisfaction,
    icon: option?.icon || CircleHelp,
    value: option?.label || '',
    profession,
    enabled: !!option,
  }
}

function getPtoBenefits(paidTimeOffWeeks: number) {
  return {
    icon: TreePalm,
    text: 'PTO',
    subText: `${paidTimeOffWeeks} ${paidTimeOffWeeks === 1 ? 'week' : 'weeks'}`,
    enabled: paidTimeOffWeeks > 0,
  }
}

function getInsuranceBenefits(insuranceHealthWellnessBenefits: string[]) {
  return {
    icon: FileHeart,
    text: 'Insurance',
    subText: insuranceHealthWellnessBenefits
      ?.map(
        (benefit) =>
          insuranceHealthBenefits.find((b) => b.id === benefit)?.label ||
          benefit,
      )
      .join(', '),
    enabled: insuranceHealthWellnessBenefits?.length > 0,
  }
}

function fmtPct(
  percentage: string,
  digits: number = 2,
  appendText = '% match',
) {
  // this is the case if Fillout returns a human readable string
  // /sentence - not a number.
  if (isNaN(Number(percentage))) {
    return percentage
  }
  return `${Number(percentage).toFixed(digits)}${appendText}`
}

function getRetirementBenefits(salary: SalaryType) {
  const subTextArray = Object.values(RetirementPlan)
    .filter((plan) => salary.retirementPlans?.includes(plan))
    .map((plan) => {
      const contribution =
        plan === RetirementPlan.Pension
          ? salary.pensionContribution
          : salary[`employer${plan}Match` as keyof SalaryType]

      return contribution
        ? `${plan} (${fmtPct(contribution, 2, plan === RetirementPlan.Pension ? '% employer contribution' : '')})`
        : plan
    })

  return {
    icon: House,
    text: 'Retirement',
    subText: subTextArray.join(', '),
    enabled: subTextArray.length > 0,
  }
}

function getCmeBenefits(cmeAllowance: number, cmeTimeWeeks: number) {
  let subText = ''
  if (cmeAllowance > 0) {
    subText += `${formatAbbreviatedDollar(cmeAllowance)} per year`
  }
  if (cmeTimeWeeks > 0) {
    if (subText.length > 0) {
      subText += ' + '
    }
    subText += `${cmeTimeWeeks} ${cmeTimeWeeks === 1 ? 'week' : 'weeks'}`
  }
  return {
    icon: TabletSmartphone,
    text: 'CME Allowance',
    subText: subText,
    enabled: subText.length > 1,
  }
}

function getMalpracticeBenefits(
  employerCoversMalpractice: boolean | null | undefined,
  tailCoverage: string | null | undefined,
) {
  const isCovered = employerCoversMalpractice ? 'Covered' : 'Not Covered'
  const tailCoverageText = tailCoverage ? tailCoverageDisplay[tailCoverage] : ''
  return {
    icon: AlertTriangle,
    text: 'Malpractice',
    subText: `${isCovered} ${tailCoverageText}`,
    enabled: typeof employerCoversMalpractice === 'boolean',
  }
}

function getParentalLeaveBenefits(
  parentalLeaveWeeks: number,
  otherBenefits: string[],
) {
  return {
    icon: Baby,
    text: 'Parental Leave',
    subText: `${parentalLeaveWeeks} weeks`,
    enabled: parentalLeaveWeeks > 0 && otherBenefits.includes(PARENTAL_LEAVE),
  }
}

function getLoanAssistanceBenefits(
  loanAssistance: number,
  otherBenefits: string[],
) {
  return {
    icon: CircleDollarSign,
    text: 'Loan Assistance',
    subText: `${formatAbbreviatedDollar(loanAssistance)}`,
    enabled: loanAssistance > 0 && otherBenefits.includes(LOAN_ASSISTANCE),
  }
}

export function getSalaryBenefits(salary: SalaryType) {
  return [
    getPtoBenefits(salary.paidTimeOffWeeks || 0),
    getInsuranceBenefits(salary.insuranceHealthWellnessBenefits || []),
    getRetirementBenefits(salary),
    getCmeBenefits(salary.cmeAllowance || 0, salary.cmeTimeWeeks || 0),
    getMalpracticeBenefits(
      salary.employerCoversMalpractice,
      salary.tailCoverage,
    ),
    getParentalLeaveBenefits(
      salary.parentalLeaveWeeks || 0,
      salary.otherNotableBenefits,
    ),
    getLoanAssistanceBenefits(
      salary.loanAssistance || 0,
      salary.otherNotableBenefits,
    ),
  ].filter((item) => item.enabled)
}

function getAgencyCommonFields(salary: SalaryType) {
  return {
    enabled:
      (salary.locums?.contractingEntity === CONTRACTING_ENTITY_AGENCY_ID &&
        salary.jobType === LOCUM_TENENS_VALUE) ||
      salary.jobType === PRN_VALUE,
  }
}

function getAgencyName(salary: SalaryType) {
  return {
    icon: Building2,
    text: 'Name',
    subText: salary.locums?.agencyName || '',
    enabled:
      salary.locums?.contractingEntity === CONTRACTING_ENTITY_AGENCY_ID &&
      !!salary.locums?.agencyName,
  }
}

function getAgencyTravel(salary: SalaryType) {
  return {
    icon: Plane,
    text: 'Travel',
    subText: salary.locums?.agencyBenefits?.some(
      (id) => id === TRAVEL_BENEFIT_ID,
    )
      ? 'Covered'
      : 'Not Covered',
    ...getAgencyCommonFields(salary),
  }
}

function getAgencyLodging(salary: SalaryType) {
  return {
    icon: Bed,
    text: 'Lodging',
    subText: salary.locums?.agencyBenefits?.some(
      (id) => id === LODGING_BENEFIT_ID,
    )
      ? 'Covered'
      : 'Not Covered',
    ...getAgencyCommonFields(salary),
  }
}

function getAgencyMalpractice(salary: SalaryType) {
  return {
    icon: AlertTriangle,
    text: 'Malpractice',
    subText: salary.locums?.agencyBenefits?.some(
      // Hard coding meal as it was mistakenly checked in,
      // when it should have been malpractice.
      (id) => id === MALPRACTICE_BENEFIT_ID || id === 'meal',
    )
      ? 'Covered'
      : 'Not Covered',
    ...getAgencyCommonFields(salary),
  }
}

function getAgencyRating(salary: SalaryType): OverviewItem {
  return {
    icon: Star,
    text: 'Rating',
    type: 'rating',
    value: salary.locums?.agencyRating || 0,
    ...getAgencyCommonFields(salary),
  }
}
